import { useEffect } from 'react';
import { TProjectEstimatePrice, TReleaseFemaleProject } from 'types';
import { GachaTicketWallet } from 'types/gacha-tickets';
import { groupTickets } from 'pages/MyPage/GachaTickets/wallet';

type Selections = NonNullable<TReleaseFemaleProject['setGachaTickets']>;
const usableTickets = (wallet?: GachaTicketWallet) =>
  groupTickets(
    wallet?.usable.filter(
      (t) =>
        t.ticketType === 'SET' &&
        t.status === 'UNUSED' &&
        !t.usedAt &&
        Date.parse(t.expiresAt) >
          Math.max(Date.now(), Date.parse(wallet.serverNow) || 0),
    ) || [],
  ).flatMap((g) => g.items);
export const hasInvalidSetTickets = (
  value: Selections,
  wallet?: GachaTicketWallet,
) =>
  value.some(
    (s) => !usableTickets(wallet).some((t) => t.ticketId === s.ticketId),
  );
export function validSetTickets(
  value: Selections,
  females: TProjectEstimatePrice['females'],
  wallet?: GachaTicketWallet,
  reassign = false,
): Selections {
  const counts = new Map<number, number>();
  const used = new Set<number>();
  const valid = value.filter((s) => {
    const count = (counts.get(s.femaleId) || 0) + 1;
    if (
      hasInvalidSetTickets([s], wallet) ||
      used.has(s.ticketId) ||
      count > (females.find((f) => f.femaleId === s.femaleId)?.setCount || 0)
    )
      return false;
    used.add(s.ticketId);
    counts.set(s.femaleId, count);
    return true;
  });
  if (!reassign || hasInvalidSetTickets(value, wallet)) return valid;
  const pool = usableTickets(wallet);
  return females.flatMap((f) =>
    pool
      .splice(0, counts.get(f.femaleId) || 0)
      .map((t) => ({ ticketId: t.ticketId, femaleId: f.femaleId })),
  );
}

export default function SetTicketSelection({
  data,
  wallet,
  value,
  onChange,
  disabled,
  pending,
  error,
  retry,
}: {
  data?: TProjectEstimatePrice;
  wallet?: GachaTicketWallet;
  value: Selections;
  onChange: (value: Selections) => void;
  disabled: boolean;
  pending: boolean;
  error: boolean;
  retry: () => void;
}) {
  const females = data?.females || [];
  const pool = usableTickets(wallet);
  const selected = validSetTickets(value, females, wallet, true);
  const unit = data?.setGachaTicketUnitPrice;
  const canAdd =
    !!unit &&
    unit > 0 &&
    (data?.setGachaTicketRemaining || 0) > selected.length * unit;
  const countCap = Math.min(
    females.reduce((sum, female) => sum + (female.setCount || 0), 0),
    pool.length,
  );
  useEffect(() => {
    if (
      !hasInvalidSetTickets(value, wallet) &&
      JSON.stringify(selected) !== JSON.stringify(value)
    )
      onChange(selected);
  }, [value, selected, wallet, onChange]);
  const change = (delta: number) => {
    const tickets = [...pool];
    let left = Math.min(countCap, Math.max(0, selected.length + delta));
    onChange(
      females.flatMap((female) => {
        const take = Math.min(left, female.setCount || 0);
        left -= take;
        return tickets
          .splice(0, take)
          .map((ticket) => ({
            ticketId: ticket.ticketId,
            femaleId: female.femaleId,
          }));
      }),
    );
  };
  const plusBlocked = disabled || selected.length >= countCap || !canAdd;
  return (
    <section
      aria-label="セット券"
      className="mt-4 border-y border-neutral-200 bg-white py-4"
    >
      <p>セット券を使う</p>
      {hasInvalidSetTickets(value, wallet) && (
        <p role="alert">
          選択した券が使えなくなりました。
          <button type="button" onClick={() => onChange([])}>
            券を選び直す
          </button>
        </p>
      )}
      {pending ? (
        <p role="status">券を確認しています</p>
      ) : error ? (
        <button type="button" onClick={retry}>
          券を取り直す
        </button>
      ) : !pool.length ? (
        <p className="text-[#756e64]">お持ちのセット券はありません</p>
      ) : (
        <div
          role="group"
          aria-label="個テコ 1 セット"
          className="mt-3 flex items-center gap-2 border-t border-neutral-200 py-3"
        >
          <div className="min-w-0 flex-1">
            <p>個テコ 1 セット</p>
            <p className="text-xs text-[#756e64]">
              お持ちの券 {pool.length} 枚
            </p>
          </div>
          <div className="flex items-center">
            <button
              type="button"
              aria-label="セット券を減らす"
              className="flex h-[44px] w-[44px] shrink-0 items-center justify-center disabled:opacity-40"
              disabled={disabled || !selected.length}
              onClick={() => change(-1)}
            >
              <span
                aria-hidden="true"
                className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-[1.5px] border-[#4a453e] leading-none"
              >
                −
              </span>
            </button>
            <span
              aria-live="polite"
              className={`w-[34px] text-center font-bold${
                selected.length ? ' text-primary-700' : ''
              }`}
            >
              {selected.length}枚
            </span>
            <button
              type="button"
              aria-label="セット券を増やす"
              className="flex h-[44px] w-[44px] shrink-0 items-center justify-center disabled:opacity-40"
              disabled={plusBlocked}
              onClick={() => {
                if (plusBlocked) return;
                change(1);
              }}
            >
              <span
                aria-hidden="true"
                className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-[1.5px] border-[#4a453e] leading-none"
              >
                ＋
              </span>
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
